<template>
  <div class="settings-subsection">
    <div class="settings-fields">
      <div class="field-group">
        <div class="flabel">
          <FieldStatusIcon :ok="Boolean(settings['timezone'])" />
          <span>Часовой пояс</span>
        </div>
        <TimeZonePicker
          :value="settings['timezone']"
          :recommendCurrentTimezone="true"
          @input="setTimezone($event)"
          label="Выбрать"
          class="timezone-picker mock-input"
          append-icon="mdi-menu-down"
          :persistent-label="false"
          hide-details
        />
      </div>

      <div class="field-group">
        <div class="flabel">
          <span>Режим работы для сервисных уведомлений по умолчанию (рекомендуем изначально подключить тестовый режим)</span>
          <SmallHint class="ml-1" :text="modeHintText" :size="16" />
        </div>
        <v-radio-group v-model="needToRedirect" row class="mt-0 mock-radios">
          <v-radio label="Тестовый" :value="true" color="mainNew" />
          <v-radio label="Боевой" :value="false" color="mainNew" />
        </v-radio-group>
      </div>

      <div v-if="needToRedirect" class="field-group">
        <div class="flabel">
          <FieldStatusIcon :ok="isValidPhone(get(settings, 'redirect.redirectPhone'))" />
          <span>Номер телефона, на который будут отправляться сервисные сообщения</span>
          <SmallHint class="ml-1" :text="redirectPhoneHintText" :size="16" />
        </div>
        <ValidationInput
          :rule="phoneValidator"
          label="+7 (999) 123-45-67"
          :value="get(settings, 'redirect.redirectPhone')"
          @input="updateSetting('redirect.redirectPhone', $event)"
          class="phone-input mock-input"
        />
      </div>

      <div class="field-group">
        <div class="flabel">
          <FieldStatusIcon :ok="isValidPhone(get(settings, 'phoneForNotification'))" />
          <span>Телефон, на который будут приходить уведомления о проблемах с авторизацией</span>
        </div>
        <ValidationInput
          :rule="phoneValidator"
          label="+7 (999) 123-45-67"
          :value="settings['phoneForNotification']"
          @input="updateSetting('phoneForNotification', $event)"
          class="phone-input mock-input"
        />
      </div>

      <div class="field-group">
        <div class="flabel">
          <FieldStatusIcon :ok="Boolean(settings['defaultChannel'])" />
          <span>Канал отправки уведомлений (по умолчанию)</span>
        </div>
        <CustomActivatorSelect
          :value="selectedChannel"
          @input="updateChannel"
          :items="channelOptions"
          item-value="value"
          item-name="label"
          :multiple="false"
          class="custom-input mock-input"
        >
          <template v-slot:activator="{ selected, _id }">
            <BaseInputOutlined
              :id="_id"
              :value="selected ? selected.label : ''"
              label="Выберите канал"
              readonly
              class="select-input"
              append-icon="mdi-menu-down"
              hide-details
            />
          </template>
        </CustomActivatorSelect>
      </div>

      <div class="field-group">
        <div class="flabel">
          <span>Номера телефонов, на которые будут приходить уведомления о плохих отзывах</span>
        </div>
        <PhoneInputGroup
          inputType="phone"
          color="#525AD5"
          :can-delete-first-item="true"
          placeholder="+7 (987) 654-32-10"
          :value="get(settings, 'claim.claimPhone')"
          @input="updateSetting('claim.claimPhone', $event)"
          class="phone-group-input"
        />
      </div>

      <div v-if="get(settings, 'cancel')" class="field-group">
        <div class="flabel">
          <span>Номера телефонов, на которые будут приходить уведомления об отменах</span>
        </div>
        <PhoneInputGroup
          color="#525AD5"
          :can-delete-first-item="true"
          placeholder="+7 (987) 654-32-10"
          :value="get(settings, 'cancel.cancelPhone')"
          @input="updateSetting('cancel.cancelPhone', $event)"
          class="phone-group-input"
        />
      </div>

      <div v-if="hasBotCrmConnector" class="field-group">
        <div class="flabel">
          <span>Группировать сообщения гостям по их визитам</span>
          <SmallHint class="ml-1" :text="visitGroupHintText" :size="16" />
        </div>
        <v-radio-group v-model="groupByVisit" row class="mt-0 mock-radios">
          <v-radio label="Да" :value="true" color="mainNew" />
          <v-radio label="Нет" :value="false" color="mainNew" />
        </v-radio-group>
      </div>

      <div v-if="hasYClientsConnector" class="field-group">
        <div class="flabel">
          <span>Отправлять только те уведомления YCLIENTS, которые начинаются с "#"</span>
        </div>
        <v-radio-group v-model="smsBySpecialCharacter" row class="mt-0 mock-radios">
          <v-radio label="Да" :value="true" color="mainNew" />
          <v-radio label="Нет" :value="false" color="mainNew" />
        </v-radio-group>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'
import SmallHint from '@/components/SmallHint.vue'
import ValidationInput from '@/components/inputs/ValidationInput.vue'
import PhoneInputGroup from '@/components/ui/PhoneInputGroup.vue'
import CustomActivatorSelect from '@/components/inputs/CustomActivatorSelect.vue'
import TimeZonePicker from '@/components/ui/TimeZonePicker.vue'
import FieldStatusIcon from '@/components/ClientNotifications/NotificationSettingsBlocks/FieldStatusIcon.vue'

export default defineComponent({
  name: 'ServiceNotificationsBlock',
  components: {
    BaseInputOutlined,
    SmallHint,
    ValidationInput,
    PhoneInputGroup,
    CustomActivatorSelect,
    TimeZonePicker,
    FieldStatusIcon,
  },
  props: {
    settings: { type: Object, required: true },
    updateSetting: { type: Function, required: true },
    setTimezone: { type: Function, required: true },
    phoneValidator: { type: Function, required: true },
    isValidPhone: { type: Function, required: true },
    get: { type: Function, required: true },

    modeHintText: { type: String, required: true },
    redirectPhoneHintText: { type: String, required: true },
    visitGroupHintText: { type: String, required: true },

    hasYClientsConnector: { type: Boolean, required: true },
    hasBotCrmConnector: { type: Boolean, required: true },

    channelOptions: { type: Array, required: true },
    selectedChannel: { type: [String, Object, null], default: null },
    updateChannel: { type: Function, required: true },
  },
  setup(props) {
    const needToRedirect = computed({
      get: () => props.get(props.settings, 'redirect.needToRedirect'),
      set: (v) => props.updateSetting('redirect.needToRedirect', v),
    })
    const groupByVisit = computed({
      get: () => props.get(props.settings, 'groupByVisit'),
      set: (v) => props.updateSetting('groupByVisit', v),
    })
    const smsBySpecialCharacter = computed({
      get: () => props.get(props.settings, 'smsBySpecialCharacter'),
      set: (v) => props.updateSetting('smsBySpecialCharacter', v),
    })

    return { needToRedirect, groupByVisit, smsBySpecialCharacter }
  },
})
</script>

<style lang="sass" scoped>
.settings-fields
  display: flex
  flex-direction: column
  gap: 14px

.field-group
  display: flex
  flex-direction: column
  gap: 9px

.flabel
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 7px
  font-size: 13px
  color: #1F2440
</style>
